<template>
    <div class="d-flex flex-column fill-height advertisement-dashboard" id="p_advertisement_dashboard">
        <v-row>
            <v-col cols="12" class="d-flex">
                <app-card :contentStyle="'padding: 8px; overflow: inherit;'" contentIsScrollable="false">
                    <template v-slot:title>
                        <v-card-title class="d-flex justify-space-between align-center title-text dark-blue-color mx-1">
                            {{ tb('Dashboard de Publicidade') }}
                        </v-card-title>
                    </template>

                    <template v-slot:content>
                        <v-overlay :value="isLoading" color="white" opacity="0.6">
                            <v-progress-circular indeterminate color="#154579" size="50"></v-progress-circular>
                        </v-overlay>

                        <v-row class="d-flex align-center px-3 pt-2" dense>
                            <v-col cols="12" sm="3">
                                <v-select
                                    v-model="filters.laboratory_id"
                                    :items="laboratoryOptions"
                                    item-text="name"
                                    item-value="id"
                                    :label="tb('Laboratório')"
                                    clearable
                                    dense
                                    hide-details
                                    :disabled="!is_admin && laboratories.length <= 1"
                                    @change="onLaboratoryChange"
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-autocomplete
                                    v-model="filters.banner_ids"
                                    :items="bannerOptions"
                                    item-text="name"
                                    item-value="id"
                                    :label="tb('Banners (campanhas) a analisar')"
                                    :no-data-text="tb('Sem banners encontrados')"
                                    multiple
                                    small-chips
                                    deletable-chips
                                    clearable
                                    dense
                                    hide-details
                                    @change="onBannerSelectionChange"
                                >
                                    <template v-slot:selection="{ item, index }">
                                        <v-chip v-if="index === 0" small close @click:close="removeBanner(item.id)">
                                            <v-avatar size="18" class="mr-1" tile v-if="item.thumbnail_url" left>
                                                <v-img :src="item.thumbnail_url"></v-img>
                                            </v-avatar>
                                            {{ item.name }}
                                        </v-chip>
                                        <span v-else-if="index === 1" class="text-caption grey--text ml-1">
                                            +{{ filters.banner_ids.length - 1 }}
                                        </span>
                                    </template>
                                    <template v-slot:item="{ item }">
                                        <v-tooltip v-if="item.thumbnail_url" right open-delay="150" content-class="banner-preview-tooltip">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-avatar size="28" class="mr-2" tile v-bind="attrs" v-on="on">
                                                    <v-img :src="item.thumbnail_url"></v-img>
                                                </v-avatar>
                                            </template>
                                            <v-img :src="item.thumbnail_url" max-width="220" max-height="220" contain></v-img>
                                        </v-tooltip>
                                        <span v-else class="mr-2" style="width: 28px; display: inline-block"></span>
                                        <span>{{ item.name }}</span>
                                    </template>
                                </v-autocomplete>
                            </v-col>
                            <v-col cols="6" sm="2">
                                <v-date-picker-menu :label="tb('Data de início')" v-model="filters.start_date" @input="fetchMetrics"></v-date-picker-menu>
                            </v-col>
                            <v-col cols="6" sm="2">
                                <v-date-picker-menu :label="tb('Data de fim')" v-model="filters.end_date" @input="fetchMetrics"></v-date-picker-menu>
                            </v-col>
                        </v-row>

                        <v-divider class="my-2"></v-divider>

                        <v-row class="px-2" dense>
                            <v-col cols="6" sm="4" md="2" v-for="kpi in kpiCards" :key="kpi.key">
                                <v-card outlined class="pa-3 fill-height">
                                    <div class="d-flex align-center">
                                        <v-avatar :color="kpi.color" size="36" class="mr-2">
                                            <v-icon color="white" size="20">{{ kpi.icon }}</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="text-caption grey--text">{{ kpi.label }}</div>
                                            <div class="text-h6 dark-blue-color">{{ kpi.value }}</div>
                                        </div>
                                    </div>
                                    <div v-if="kpi.delta !== null" class="text-caption grey--text mt-1">
                                        <span :class="kpi.delta >= 0 ? 'green--text' : 'red--text'">
                                            <v-icon x-small :color="kpi.delta >= 0 ? 'green' : 'red'">{{ kpi.delta >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                                            {{ Math.abs(kpi.delta).toFixed(2) }}{{ kpi.deltaSuffix }}
                                        </span>
                                        {{ $t('ads.vsPrevious') }}
                                    </div>
                                </v-card>
                            </v-col>
                        </v-row>

                        <v-row class="px-2 mt-2" dense align="stretch">
                            <v-col cols="12" md="4" class="d-flex">
                                <v-card outlined class="pa-2 fill-height flex-grow-1">
                                    <div class="text-subtitle-2 px-2 pt-1">{{ tb('Funil de Conversão') }}</div>

                                    <div class="d-flex justify-end px-2 pt-2 text-caption grey--text" style="gap: 8px">
                                        <span style="width: 70px" class="text-right">{{ tb('Taxa') }}</span>
                                        <span style="width: 90px" class="text-right">{{ tb('vs período ant.') }}</span>
                                    </div>

                                    <div
                                        v-for="stage in funnelStages"
                                        :key="stage.key"
                                        class="d-flex align-center pr-2"
                                        :style="{ gap: '8px', borderRadius: '6px', background: stage.color + '14', marginBottom: '6px', minHeight: '44px' }"
                                    >
                                        <div class="funnel-bar-wrapper">
                                            <div
                                                class="d-flex align-center px-3 py-2 white--text funnel-bar"
                                                :style="{ background: stage.color, width: stage.widthPct + '%' }"
                                            >
                                                <v-icon color="white" size="18" class="mr-2">{{ stage.icon }}</v-icon>
                                                <div>
                                                    <div class="text-caption" style="opacity: 0.85">{{ stage.label }}</div>
                                                    <div class="text-subtitle-2 font-weight-bold">{{ stage.value }}</div>
                                                </div>
                                            </div>
                                        </div>

                                        <span style="width: 70px" class="text-right text-body-2">{{ stage.taxa !== null ? stage.taxa + '%' : '-' }}</span>

                                        <span style="width: 90px" class="text-right text-caption">
                                            <template v-if="stage.delta === null">-</template>
                                            <span v-else :class="stage.delta >= 0 ? 'green--text' : 'red--text'">
                                                <v-icon x-small :color="stage.delta >= 0 ? 'green' : 'red'">{{ stage.delta >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                                                {{ Math.abs(stage.delta).toFixed(2) }} pp
                                            </span>
                                        </span>
                                    </div>
                                </v-card>
                            </v-col>
                            <v-col cols="12" md="4" class="d-flex">
                                <v-card outlined class="pa-2 fill-height flex-grow-1">
                                    <div class="text-subtitle-2 px-2 pt-1">{{ tb('Evolução ao longo do período') }}</div>
                                    <highcharts :options="evolutionOptions"></highcharts>
                                </v-card>
                            </v-col>
                            <v-col cols="12" md="4" class="d-flex">
                                <v-card
                                    class="fill-height flex-grow-1 d-flex flex-column pa-4 white--text roas-card"
                                    style="background: linear-gradient(135deg, #4a6cf7 0%, #7b2ff7 100%); border-radius: 12px"
                                >
                                    <div class="text-subtitle-2 mb-3">{{ tb('Retorno sobre o investimento') }}</div>

                                    <div class="d-flex" style="gap: 8px">
                                        <div class="flex-grow-1 pa-2 text-center roas-box">
                                            <div class="text-caption" style="opacity: 0.8">{{ tb('Investimento') }}</div>
                                            <div class="text-h6 font-weight-bold">{{ formatIntegerPrice(kpis.investment, 2, 2) }}</div>
                                        </div>
                                        <div class="flex-grow-1 pa-2 text-center roas-box">
                                            <div class="text-caption" style="opacity: 0.8">{{ tb('Receita gerada') }}</div>
                                            <div class="text-h6 font-weight-bold">{{ formatIntegerPrice(kpis.revenue, 2, 2) }}</div>
                                        </div>
                                    </div>

                                    <div class="d-flex flex-column align-center flex-grow-1 justify-center py-2">
                                        <svg width="150" height="150" viewBox="0 0 150 150">
                                            <circle cx="75" cy="75" r="62" fill="none" stroke="rgba(255,255,255,0.25)" stroke-width="10" />
                                            <circle
                                                v-if="Number(kpis.roas) > 0"
                                                cx="75"
                                                cy="75"
                                                r="62"
                                                fill="none"
                                                :stroke="roasRingColor"
                                                stroke-width="10"
                                                stroke-linecap="round"
                                                :stroke-dasharray="roasRingDashArray"
                                                transform="rotate(-90 75 75)"
                                            />
                                            <text x="75" y="72" text-anchor="middle" fill="white" :font-size="roasValueFontSize" font-weight="bold">{{ kpis.roas }}x</text>
                                            <text x="75" y="92" text-anchor="middle" fill="white" font-size="12" style="opacity: 0.85">ROAS</text>
                                        </svg>

                                        <div class="text-caption text-center mt-2" style="opacity: 0.9; max-width: 240px">
                                            {{ $t('ads.roasSentence', { roas: kpis.roas }) }}
                                        </div>
                                    </div>

                                    <div class="d-flex justify-space-between roas-box pa-3">
                                        <div>
                                            <div class="text-caption" style="opacity: 0.8">{{ tb('ROAS médio da plataforma') }}</div>
                                            <div class="text-subtitle-1 font-weight-bold">{{ platform_roas }}x</div>
                                        </div>
                                        <div class="text-right">
                                            <div class="text-caption" style="opacity: 0.8">{{ tb('A sua seleção está') }}</div>
                                            <div class="text-body-2">
                                                <span :class="roasVsPlatform >= 0 ? 'green--text text--lighten-2' : 'red--text text--lighten-2'" class="font-weight-bold">
                                                    {{ roasVsPlatform >= 0 ? '+' : '' }}{{ roasVsPlatform }}%
                                                </span>
                                                {{ $t(roasVsPlatform >= 0 ? 'ads.aboveAverage' : 'ads.belowAverage') }}
                                            </div>
                                        </div>
                                    </div>
                                </v-card>
                            </v-col>
                        </v-row>

                        <v-row class="px-2 mt-2" dense>
                            <v-col cols="12" md="6">
                                <v-card outlined class="pa-2 d-flex flex-column fill-height">
                                    <div class="text-subtitle-2 px-2 pt-1">{{ tb('Performance por produto') }}</div>

                                    <div class="d-flex justify-space-between px-2 pt-2 text-caption grey--text" style="white-space: nowrap">
                                        <span>{{ tb('Produto') }}</span>
                                        <span class="d-flex">
                                            <span style="width: 90px" class="text-right pl-3">{{ tb('Compras') }}</span>
                                            <span style="width: 110px" class="text-right pl-3">{{ tb('Receita') }}</span>
                                        </span>
                                    </div>

                                    <div v-if="!performance_by_product.length" class="text-caption grey--text px-2 py-4 text-center">
                                        {{ $t('ads.noData') }}
                                    </div>

                                    <div
                                        v-for="item in performance_by_product.slice(0, 7)"
                                        :key="item.code"
                                        class="d-flex justify-space-between align-center px-2 py-2 text-caption"
                                        style="border-top: 1px solid #eee"
                                    >
                                        <div class="d-flex align-center" style="min-width: 0">
                                            <v-avatar size="28" class="mr-2" tile>
                                                <v-img :src="item.image"></v-img>
                                            </v-avatar>
                                            <span class="text-truncate">{{ item.designation }}</span>
                                        </div>
                                        <span class="d-flex" style="white-space: nowrap">
                                            <span style="width: 90px" class="text-right pl-3">{{ item.compras }}</span>
                                            <span style="width: 110px" class="text-right pl-3">{{ item.receita_formatted }}</span>
                                        </span>
                                    </div>

                                    <v-spacer></v-spacer>

                                    <div class="text-center pt-2" v-if="performance_by_product.length">
                                        <a class="text-decoration-none" @click="openExpandedTable('Performance por produto', productHeaders, 'products')">
                                            {{ tb('Ver todos os produtos') }} <v-icon small color="#154579">mdi-arrow-right</v-icon>
                                        </a>
                                    </div>
                                </v-card>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-card outlined class="pa-2">
                                    <div class="text-subtitle-2 px-2 pt-1">{{ tb('Performance por segmento') }}</div>
                                    <highcharts :options="segmentOptions"></highcharts>
                                </v-card>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-card outlined class="pa-2">
                                    <div class="text-subtitle-2 px-2 pt-1">{{ tb('Performance geográfica (por distrito)') }}</div>
                                    <highcharts v-if="mapData" :options="districtMapOptions" constructor-type="mapChart"></highcharts>
                                    <div v-else class="text-caption grey--text px-2 py-4 text-center">{{ tb('A carregar mapa...') }}</div>
                                </v-card>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-card outlined class="pa-2 d-flex flex-column fill-height">
                                    <div class="text-subtitle-2 px-2 pt-1">{{ tb('Top clientes impactados') }}</div>

                                    <div class="d-flex justify-space-between px-2 pt-2 text-caption grey--text" style="white-space: nowrap">
                                        <span>{{ tb('Cliente') }}</span>
                                        <span class="d-flex">
                                            <span style="width: 70px" class="text-right pl-3">{{ tb('Cliques') }}</span>
                                            <span style="width: 90px" class="text-right pl-3">{{ tb('Compras') }}</span>
                                            <span style="width: 110px" class="text-right pl-3">{{ tb('Receita') }}</span>
                                        </span>
                                    </div>

                                    <div v-if="!top_clients.length" class="text-caption grey--text px-2 py-4 text-center">
                                        {{ $t('ads.noData') }}
                                    </div>

                                    <div
                                        v-for="item in top_clients.slice(0, 8)"
                                        :key="item.branch_id"
                                        class="d-flex justify-space-between align-center px-2 py-2 text-caption"
                                        style="border-top: 1px solid #eee"
                                    >
                                        <span class="text-truncate">{{ item.cliente }}</span>
                                        <span class="d-flex" style="white-space: nowrap">
                                            <span style="width: 70px" class="text-right pl-3">{{ item.cliques }}</span>
                                            <span style="width: 90px" class="text-right pl-3">{{ item.compras }}</span>
                                            <span style="width: 110px" class="text-right pl-3">{{ item.receita_formatted }}</span>
                                        </span>
                                    </div>

                                    <v-spacer></v-spacer>

                                    <div class="text-center pt-2" v-if="top_clients.length">
                                        <a class="text-decoration-none" @click="openExpandedTable('Top clientes impactados', clientHeaders, 'clients')">
                                            {{ tb('Ver todos os clientes') }} <v-icon small color="#154579">mdi-arrow-right</v-icon>
                                        </a>
                                    </div>
                                </v-card>
                            </v-col>
                        </v-row>

                        <app-dialog :value="isExpandedTableOpen" @input="isExpandedTableOpen = $event" :title="expandedTable.title" width="1200" content-style="overflow-x: auto">
                            <template v-slot:content>
                                <v-data-table
                                    class="expanded-table"
                                    :headers="tHeaders(expandedTable.headers)"
                                    :items="expandedTable.items"
                                    :loading="isExpandedTableLoading"
                                    :items-per-page="15"
                                    height="400"
                                    fixed-header
                                    :footer-props="{
                                        itemsPerPageOptions: [15, 50, 100, -1],
                                        itemsPerPageText: $t('table.itemsPerPage'),
                                        itemsPerPageAllText: $t('table.all'),
                                    }"
                                    :no-data-text="$t('ads.noData')"
                                ></v-data-table>
                            </template>

                            <template v-slot:buttons>
                                <v-btn text outlined class="myprovet-btn-cancel" @click="isExpandedTableOpen = false">{{ tb('Fechar') }}</v-btn>
                            </template>
                        </app-dialog>
                    </template>
                </app-card>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import { i18n } from '@/i18n';
import MainLayout from './../../Layouts/MainLayout';
import { axios } from '@/Services/Backend';
import * as Highcharts from 'highcharts';
import 'highcharts/modules/map';
import 'highcharts/modules/exporting';
import 'highcharts/modules/accessibility';
import 'highcharts/modules/export-data';
import 'highcharts/modules/no-data-to-display';

export default {
    layout: MainLayout,

    props: {
        laboratories: { type: Array, default: () => [] },
        is_admin: { type: Boolean, default: false },
    },

    data() {
        const today = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const todayFormatted = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

        const defaultDateRange = {
            start_date: `${today.getFullYear()}-${pad(today.getMonth() + 1)}-01`,
            end_date: todayFormatted,
        };

        return {
        defaultDateRange,
        filters: {
            laboratory_id: null,
            banner_ids: [],
            start_date: defaultDateRange.start_date,
            end_date: defaultDateRange.end_date,
        },
        isLoading: false,
        kpis: {
            active_banners: 0,
            impressions: 0,
            clicks: 0,
            ctr: 0,
            product_views: 0,
            added_to_cart: 0,
            orders: 0,
            orders_conversion_rate: 0,
            order_lines: 0,
            revenue: 0,
            investment: 0,
            roas: 0,
        },
        previous_kpis: null,
        platform_roas: 0,
        evolution: [],
        performance_by_product: [],
        performance_by_segment: [],
        performance_by_district: [],
        top_clients: [],
        banners: [],
        mapData: null,

        isExpandedTableOpen: false,
        isExpandedTableLoading: false,
        expandedTable: { title: '', headers: [], items: [] },

        productHeaders: [
            { text: 'Cód.', value: 'code' },
            { text: 'Produto', value: 'designation' },
            { text: 'Compras', value: 'compras' },
            { text: 'Receita', value: 'receita_formatted' },
        ],
        clientHeaders: [
            { text: 'Cliente', value: 'cliente' },
            { text: 'Cliques', value: 'cliques' },
            { text: 'Compras', value: 'compras' },
            { text: 'Receita', value: 'receita_formatted' },
        ],
        };
    },

    computed: {
        laboratoryOptions() {
            return this.laboratories;
        },
        bannerOptions() {
            return this.banners;
        },
        roasRingDashArray() {
            const circumference = 2 * Math.PI * 62;
            const pct = Math.min(100, (Number(this.kpis.roas) / 10) * 100);

            return `${(circumference * pct) / 100} ${circumference}`;
        },
        roasRingColor() {
            const roas = Number(this.kpis.roas);

            if (roas < 1) return '#e53935';
            if (roas <= 3) return '#ffb74d';

            return '#8bc34a';
        },
        roasValueFontSize() {
            const length = `${this.kpis.roas}x`.length;

            if (length <= 5) return 26;
            if (length <= 7) return 21;

            return 17;
        },
        roasVsPlatform() {
            if (!this.platform_roas) return 0;

            return Math.round(((this.kpis.roas - this.platform_roas) / this.platform_roas) * 100);
        },
        kpiCards() {
            const prev = this.previous_kpis;

            return [
                { key: 'active_banners', label: 'Banners ativos', value: this.formatNumber(this.kpis.active_banners), icon: 'mdi-bullhorn-outline', color: '#607d8b', delta: null, deltaSuffix: '' },
                { key: 'impressions', label: this.tb('Impressões'), value: this.formatNumber(this.kpis.impressions), icon: 'mdi-eye-outline', color: '#1e88e5', delta: this.percentDelta(this.kpis.impressions, prev?.impressions), deltaSuffix: '%' },
                { key: 'clicks', label: this.tb('Cliques'), value: this.formatNumber(this.kpis.clicks), icon: 'mdi-cursor-default-click-outline', color: '#2e7d32', delta: this.percentDelta(this.kpis.clicks, prev?.clicks), deltaSuffix: '%' },
                { key: 'ctr', label: 'CTR', value: `${this.kpis.ctr}%`, icon: 'mdi-trending-up', color: '#f9a825', delta: this.pointDelta(this.kpis.ctr, prev?.ctr), deltaSuffix: 'pp' },
                { key: 'orders', label: this.tb('Compras'), value: this.formatNumber(this.kpis.orders), icon: 'mdi-cart-outline', color: '#1565c0', delta: this.percentDelta(this.kpis.orders, prev?.orders), deltaSuffix: '%' },
                { key: 'revenue', label: 'Receita gerada', value: this.formatIntegerPrice(this.kpis.revenue, 2, 2), icon: 'mdi-cash', color: '#2e7d32', delta: this.percentDelta(this.kpis.revenue, prev?.revenue), deltaSuffix: '%' },
            ];
        },
        funnelStages() {
            const prev = this.previous_kpis;
            const rate = (numerator, denominator) => (denominator > 0 ? Math.round((numerator / denominator) * 10000) / 100 : 0);

            const productViewsRate = rate(this.kpis.product_views, this.kpis.clicks);
            const addedToCartRate = rate(this.kpis.added_to_cart, this.kpis.product_views);
            const prevProductViewsRate = prev ? rate(prev.product_views, prev.clicks) : null;
            const prevAddedToCartRate = prev ? rate(prev.added_to_cart, prev.product_views) : null;

            // Larguras fixas decrescentes só para dar efeito visual de funil —
            // os valores reais entre fases diferem em ordens de magnitude,
            // por isso não são proporcionais ao valor.
            const widths = [100, 85, 70, 58, 45];

            return [
                {
                    key: 'impressions',
                    label: this.tb('Impressões'),
                    icon: 'mdi-eye-outline',
                    color: '#1e88e5',
                    value: this.formatNumber(this.kpis.impressions),
                    widthPct: widths[0],
                    taxa: null,
                    delta: null,
                },
                {
                    key: 'clicks',
                    label: this.tb('Cliques'),
                    icon: 'mdi-cursor-default-click-outline',
                    color: '#7c4dff',
                    value: this.formatNumber(this.kpis.clicks),
                    widthPct: widths[1],
                    taxa: this.kpis.ctr,
                    delta: this.pointDelta(this.kpis.ctr, prev?.ctr),
                },
                {
                    key: 'product_views',
                    label: this.tb('Produtos visualizados'),
                    icon: 'mdi-eye-check-outline',
                    color: '#00897b',
                    value: this.formatNumber(this.kpis.product_views),
                    widthPct: widths[2],
                    taxa: productViewsRate,
                    delta: this.pointDelta(productViewsRate, prevProductViewsRate),
                },
                {
                    key: 'added_to_cart',
                    label: this.tb('Adicionados ao carrinho'),
                    icon: 'mdi-cart-plus',
                    color: '#f9a825',
                    value: this.formatNumber(this.kpis.added_to_cart),
                    widthPct: widths[3],
                    taxa: addedToCartRate,
                    delta: this.pointDelta(addedToCartRate, prevAddedToCartRate),
                },
                {
                    key: 'orders',
                    label: this.tb('Compras'),
                    icon: 'mdi-cart-outline',
                    color: '#d81b60',
                    value: this.formatNumber(this.kpis.orders),
                    widthPct: widths[4],
                    taxa: this.kpis.orders_conversion_rate,
                    delta: this.pointDelta(this.kpis.orders_conversion_rate, prev?.orders_conversion_rate),
                },
            ];
        },
        evolutionOptions() {
            const dates = this.evolution.map((it) => it.date);

            return this.chartBaseOptions({
                chart: { type: 'line', marginTop: window.innerWidth <= 600 ? 90 : 65 },
                legend: {
                    verticalAlign: 'top',
                    align: 'center',
                    layout: 'horizontal',
                    margin: 20,
                },
                xAxis: { categories: dates },
                yAxis: [
                    { title: { text: '' } },
                    {
                        title: { text: '' },
                        labels: {
                            formatter: function () {
                                return `${Highcharts.numberFormat(this.value, 0, ',', '.')} €`;
                            },
                        },
                        opposite: true,
                    },
                ],
                tooltip: { shared: true },
                series: [
                    { name: this.tb('Impressões'), data: this.evolution.map((it) => it.impressions), yAxis: 0 },
                    { name: this.tb('Cliques'), data: this.evolution.map((it) => it.clicks), yAxis: 0 },
                    { name: this.tb('Compras'), data: this.evolution.map((it) => it.orders), yAxis: 0 },
                    { name: this.tb('Receita (€)'), data: this.evolution.map((it) => it.revenue), yAxis: 1, color: '#fb8c00' },
                ],
            });
        },
        segmentOptions() {
            const palette = ['#1565c0', '#1e88e5', '#64b5f6', '#90caf9', '#ffb74d', '#ffa726', '#fb8c00', '#f57c00', '#ffcc80', '#ffe0b2'];

            return this.chartBaseOptions({
                chart: { type: 'pie', height: 420, spacingBottom: 20 },
                colors: palette,
                legend: {
                    enabled: true,
                    align: 'center',
                    verticalAlign: 'bottom',
                    layout: 'horizontal',
                    floating: false,
                    symbolRadius: 6,
                    itemStyle: { fontSize: '12px' },
                },
                tooltip: {
                    pointFormatter: function () {
                        return `<b>${this.name}</b><br/>${i18n.t('ads.revenue')}: ${Highcharts.numberFormat(this.y, 2, ',', '.')} € (${this.percentage.toFixed(1)}%)`;
                    },
                },
                plotOptions: {
                    pie: {
                        innerSize: '65%',
                        borderRadius: 4,
                        borderWidth: 3,
                        borderColor: '#fff',
                        showInLegend: true,
                        dataLabels: {
                            enabled: true,
                            distance: -30,
                            format: '{point.percentage:.1f}%',
                            style: { fontSize: '11px', fontWeight: '600', textOutline: 'none' },
                        },
                    },
                },
                series: [
                    {
                        name: this.tb('Receita'),
                        data: this.performance_by_segment.map((it) => ({ name: it.segmento, y: Number(it.receita) })),
                    },
                ],
            });
        },
        districtMapOptions() {
            const totalRevenue = this.performance_by_district.reduce((sum, it) => sum + Number(it.receita), 0);

            return this.chartBaseOptions({
                colorAxis: {
                    min: 0,
                    minColor: '#e3f2fd',
                    maxColor: '#1565c0',
                    stops: [
                        [0, '#e3f2fd'],
                        [0.5, '#64b5f6'],
                        [1, '#1565c0'],
                    ],
                },
                borderColor: '#fff',
                borderWidth: 1,
                tooltip: {
                    pointFormatter: function () {
                        const pct = totalRevenue ? (this.value / totalRevenue) * 100 : 0;

                        return `<b>${this.name}</b><br/>${i18n.t('ads.revenue')}: ${Highcharts.numberFormat(this.value, 2, ',', '.')} € (${pct.toFixed(2)}%)`;
                    },
                },
                series: [
                    {
                        name: this.tb('Receita por distrito'),
                        mapData: this.mapData,
                        data: this.performance_by_district.map((it) => ({ name: it.distrito, value: Number(it.receita) })),
                        joinBy: 'name',
                        //borderColor: '#fff',
                        //borderWidth: 1,
                        states: { hover: { color: '#0d47a1' } },
                        dataLabels: { enabled: false },
                    },
                ],
            });
        },
    },

    mounted() {
        // No refresh direto (não navegação SPA), este componente monta antes do MainLayout
        // (o pai monta depois do filho) — o $nextTick garante que o listener já está registado.
        this.$nextTick(() => {
            window.EventBus.$emit('collapse-categories-card', true);
        });

        if (!this.is_admin && this.laboratories.length === 1) {
            this.filters.laboratory_id = this.laboratories[0].id;
        }

        this.fetchMetrics();
        this.fetchMapData();
    },

    methods: {
        formatNumber(value) {
            return Number(value || 0).toLocaleString('pt-PT');
        },

        percentDelta(current, previous) {
            if (previous === null || previous === undefined || previous === 0) return null;

            return ((current - previous) / previous) * 100;
        },

        pointDelta(current, previous) {
            if (previous === null || previous === undefined) return null;

            return current - previous;
        },

        chartBaseOptions(overrides) {
            return {
                credits: { enabled: false },
                title: { text: '' },
                lang: { noData: this.isLoading ? '' : this.$t('charts.noData') },
                ...overrides,
            };
        },

        openExpandedTable(title, headers, expandKey) {
            this.expandedTable = { title, headers, items: [] };
            this.isExpandedTableOpen = true;
            this.isExpandedTableLoading = true;

            const responseField = expandKey === 'products' ? 'performance_by_product' : 'top_clients';

            axios
                .post('/api/statistics/advertisement-dashboard', { ...this.filters, expand: expandKey })
                .then(({ data }) => {
                    this.expandedTable.items = data[responseField].map((it) => ({
                        ...it,
                        receita_formatted: this.formatIntegerPrice(it.receita, 2, 2),
                    }));
                })
                .finally(() => {
                    this.isExpandedTableLoading = false;
                });
        },

        fetchMapData() {
            axios
                .get('https://code.highcharts.com/mapdata/countries/pt/pt-all.geo.json')
                .then(({ data }) => {
                    this.mapData = data;
                })
                .catch(() => {
                    this.mapData = null;
                });
        },

        onLaboratoryChange() {
            this.filters.banner_ids = [];
            this.applyBannerDateRange();
            this.fetchMetrics();
        },

        removeBanner(bannerId) {
            this.filters.banner_ids = this.filters.banner_ids.filter((id) => id !== bannerId);
            this.applyBannerDateRange();
            this.fetchMetrics();
        },

        onBannerSelectionChange() {
            this.applyBannerDateRange();
            this.fetchMetrics();
        },

        applyBannerDateRange() {
            if (this.filters.banner_ids.length !== 1) {
                this.filters.start_date = this.defaultDateRange.start_date;
                this.filters.end_date = this.defaultDateRange.end_date;
                return;
            }

            const banner = this.banners.find((b) => b.id === this.filters.banner_ids[0]);

            if (banner && banner.use_period && banner.start_date && banner.end_date) {
                this.filters.start_date = banner.start_date.slice(0, 10);
                this.filters.end_date = banner.end_date.slice(0, 10);
            } else {
                this.filters.start_date = this.defaultDateRange.start_date;
                this.filters.end_date = this.defaultDateRange.end_date;
            }
        },

        fetchMetrics() {
            this.isLoading = true;

            axios
                .post('/api/statistics/advertisement-dashboard', this.filters)
                .then(({ data }) => {
                    this.kpis = data.kpis;
                    this.previous_kpis = data.previous_kpis;
                    this.platform_roas = data.platform_roas;
                    this.evolution = data.evolution;
                    this.banners = data.banners;
                    this.performance_by_product = data.performance_by_product.map((it) => ({
                        ...it,
                        receita_formatted: this.formatIntegerPrice(it.receita, 2, 2),
                    }));
                    this.performance_by_segment = data.performance_by_segment;
                    this.performance_by_district = data.performance_by_district;
                    this.top_clients = data.top_clients.map((it) => ({
                        ...it,
                        receita_formatted: this.formatIntegerPrice(it.receita, 2, 2),
                    }));
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
    },
};
</script>

<style scoped>
.expanded-table ::v-deep td {
    white-space: normal !important;
    text-overflow: unset !important;
    overflow: visible !important;
}

.funnel-bar {
    border-radius: 6px;
    transition: width 0.2s ease;
    min-height: 44px;
}

.funnel-bar-wrapper {
    flex: 1 1 auto;
    min-width: 0;
}

.roas-box {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 8px;
}
</style>

<style>
.banner-preview-tooltip {
    background: white !important;
    padding: 8px !important;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);
    opacity: 1 !important;
}
</style>
